import moment from 'moment';
import { ICSVRow } from 'interfaces/ICSV';
import { ISession } from 'interfaces/session/ISession';
import { v1 as uuid } from 'uuid';
import { emptyGuid, emptyPlayer } from 'constants/Constants';
import { TopBottom, BallFinalKinds, HitTypes, IBallFinal, IPlayer, baseballLogicService } from '@trackman/baseball-shared-components';
import { findTeamInSession, getBallFinalTrackId } from './mergeHelpers';
import { undefinedValue } from '../../constants/Constants';
import { csvImportUtils } from '../../services/helper-services/csvImportUtils';
import { IMergedPlayWithBallFinal } from 'interfaces/sessionVerification/plays/IPlayWithBallFinal';

export const createNewPlayBasedOnRow = (
  row: ICSVRow,
  session: ISession,
  allBallFinals: IBallFinal[],
  homeTeamRoster: IPlayer[],
  awayTeamRoster: IPlayer[]
): any => {
  const ballFinals = row.playid ? allBallFinals.filter((bf) => bf.playId === row.playid) : [];
  const pitchingRoster = baseballLogicService.isHomeTeamBatting(row.topbottom === TopBottom.Top) ? awayTeamRoster : homeTeamRoster;
  const battingRoster = baseballLogicService.isHomeTeamBatting(row.topbottom === TopBottom.Top) ? homeTeamRoster : awayTeamRoster;
  const catcher = csvImportUtils.findPlayerInRoster(pitchingRoster, row.catcher, row.catcherid);
  const pitcher = csvImportUtils.findPlayerInRoster(pitchingRoster, row.pitcher, row.pitcherid);
  const batter = csvImportUtils.findPlayerInRoster(battingRoster, row.batter, row.batterid);
  return {
    ballFinals,
    play: {
      createdInStadiumUtc: row.utcdatetime,
      createdInStadiumLocal: row.localdatetime,
      updatedInStadiumUtc: moment().utc(false).toJSON(),
      updatedInStadiumLocal: moment().toJSON(),
      trackStartTimeLocal: row.localdatetime,
      trackStartTimeUtc: row.utcdatetime,
      id: uuid(),
      time: moment().utc(false).toJSON(),
      version: '1.0.0', // Current tagging version
      sessionId: session.id,
      playId: row.playid,
      taggerBehavior: {
        manuallyAddedByTagger: true,
        isWarmup: false,
        playSequenceNumber: row.pitchno,
        sequenceNumber: row.pitchno || -1,
        isDeleted: false,
        pAofInning: row.paofinning,
        pitchOfPA: row.pitchofpa,
      },
      players: {
        batter: {
          id: batter?.id || emptyGuid,
          nameRef: row.batter,
          position: undefinedValue,
          battingHandedness: row.batterside,
          isPlaceholder: false,
          foreignIds: batter?.foreignIds || [row.batterid],
        },
        catcher: {
          id: catcher?.id || emptyGuid,
          nameRef: row.catcher,
          position: 'C',
          isPlaceholder: false,
          foreignIds: catcher?.foreignIds || [row.catcherid],
          pitchingHandedness: row.catcherthrows,
        },
        pitcher: {
          id: pitcher?.id || emptyGuid,
          nameRef: row.pitcher,
          position: 'P',
          isPlaceholder: false,
          pitchingHandedness: row.pitcherthrows,
          foreignIds: pitcher?.foreignIds || [row.pitcherid],
        },
      },
      gameState: {
        homeTeamScore: 0,
        awayTeamScore: 0,
        isHomeTeamBatting: row.topbottom !== TopBottom.Top,
        inning: row.inning,
        topBottom: row.topbottom,
        balls: row.balls,
        strikes: row.strikes,
        outs: row.outs,
      },
      pitchTag: {
        ballFinalTrackId: getBallFinalTrackId(ballFinals, BallFinalKinds.Pitch),
        pitcherSet: undefined,
        pitchType: row.taggedpitchtype,
        pitchCall: row.pitchcall,
      },
      hitTag: row.taggedhittype
        ? {
            hitType: row.taggedhittype || HitTypes.Undefined,
            hitQuality: '',
            ballFinalTrackId: getBallFinalTrackId(ballFinals, BallFinalKinds.Hit),
          }
        : null,
      catcherThrowTag: {
        ballFinalTrackId: getBallFinalTrackId(ballFinals, BallFinalKinds.CatcherThrow),
        fielder: { ...emptyPlayer },
        thrownPosition: undefinedValue,
      },
      playResult: {
        outs: row.outsonplay,
        runs: row.runsscored,
        result: row.playresult,
      },
      note: row.notes,
      kOrBB: row.korbb,
      pitchingTeam: findTeamInSession(row.pitcherteam, session),
      battingTeam: findTeamInSession(row.batterteam, session),
      playLineup: {
        pitchingTeam: [],
        battingTeam: [],
      },
    },
    isChanged: true,
    isPlayerTrackingChanged: true,
    isBallTrackingChanged: true,
    isMarkedForDeletion: false,
    isCreated: true,
    changedValues: [],
  };
};
